/*
 * Purpose: L1 interpretation confirm — Express shortcut or Guided full trail
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, ArrowRight, CheckCircle2, Sparkles, Zap } from 'lucide-react';
import type { IntentInterpretation } from '@/services/autoblocks/intentInterpretation';
import type { ComposeMode } from '@/services/autoblocks/missionTypes';
import { composeModeDescription, composeModeLabel } from '@/services/autoblocks/flowMode';

interface IntentConfirmPanelProps {
  interpretation: IntentInterpretation;
  composeMode: ComposeMode | null;
  isWorking?: boolean;
  onConfirmGuided: () => void;
  onExpressProceed: () => void;
  onReinterpret?: () => void;
}

export function IntentConfirmPanel({
  interpretation,
  composeMode,
  isWorking,
  onConfirmGuided,
  onExpressProceed,
  onReinterpret,
}: IntentConfirmPanelProps) {
  const confidencePct = Math.round(interpretation.confidence * 100);
  const canExpress =
    interpretation.gaps.length === 0 || interpretation.suggestedComposeMode === 'express';
  const suggested = composeMode ?? interpretation.suggestedComposeMode;

  return (
    <Card className="ring-1 ring-primary/25">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Confirm your outcome</CardTitle>
          <Badge variant="secondary">{confidencePct}% confidence</Badge>
          {interpretation.plannerSource === 'llm' && (
            <Badge variant="default" className="text-[10px]">
              AI interpreted
            </Badge>
          )}
        </div>
        <CardDescription>{interpretation.interpretedGoal}</CardDescription>
        <Progress value={confidencePct} className="h-1.5 mt-2" />
      </CardHeader>
      <CardContent className="space-y-4">
        {interpretation.assumptions.map((a) => (
          <p key={a} className="text-xs text-muted-foreground flex gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            {a}
          </p>
        ))}
        {interpretation.gaps.map((g) => (
          <p key={g} className="text-xs text-amber-600 dark:text-amber-400 flex gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            {g}
          </p>
        ))}

        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div className="rounded-md border p-3 space-y-1">
            <p className="font-semibold flex items-center gap-1">
              <Zap className="h-3.5 w-3.5" />
              {composeModeLabel('express')}
            </p>
            <p className="text-muted-foreground">{composeModeDescription('express')}</p>
          </div>
          <div className="rounded-md border p-3 space-y-1">
            <p className="font-semibold flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" />
              {composeModeLabel('guided')}
            </p>
            <p className="text-muted-foreground">{composeModeDescription('guided')}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {canExpress && suggested === 'express' && (
            <Button onClick={onExpressProceed} disabled={isWorking} className="w-full sm:w-auto">
              <Zap className="h-4 w-4 mr-2" />
              {isWorking ? 'Running Express…' : 'Express — confirm & plan'}
            </Button>
          )}
          <Button
            variant={suggested === 'guided' ? 'default' : 'secondary'}
            onClick={onConfirmGuided}
            disabled={isWorking}
            className="w-full sm:w-auto"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {isWorking ? 'Planning…' : 'Guided — plan Mission'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          {onReinterpret && (
            <Button variant="ghost" size="sm" onClick={onReinterpret} disabled={isWorking}>
              Re-interpret
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
